import {
  Phone, Mail, MapPin, AlertTriangle, MessageSquare, Copy, BadgeCheck, Lightbulb,
} from "lucide-react";
import { CATEGORY_STYLE } from "@/lib/format";

function Info({ label, value, inline }) {
  if (inline) {
    return (
      <div className="py-1.5">
        <span className="text-xs text-[#9aa6c0]">{label}</span>
        <p className="text-[#1a2d56] font-medium text-sm">{value || "—"}</p>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-[#9aa6c0]">{label}</span>
      <p className="text-[#4b5b7c] text-sm mt-1 leading-relaxed">{value || "—"}</p>
    </div>
  );
}

function Badge({ children, cls }) {
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${cls || "bg-slate-100 text-slate-600"}`}>{children}</span>;
}

const MEMBER_BADGE = {
  current: "bg-emerald-100 text-emerald-700",
  past: "bg-amber-100 text-amber-700",
};
const memberBadgeClass = (status) => MEMBER_BADGE[status] || "bg-slate-100 text-slate-600";

export function LeadInfoPanel({ lead, onCopyScript }) {
  const ex = lead.extracted || {};
  return (
    <div className="lg:col-span-2 space-y-6">
      <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-head text-2xl font-bold text-[#1a2d56]" data-testid="lead-name">
              {lead.first_name} {lead.last_name}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#4b5b7c]">
              <span className="inline-flex items-center gap-1.5"><Mail size={14} /> {lead.email}</span>
              {lead.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {lead.phone}</span>}
              {lead.postal_code && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {lead.postal_code}</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="font-head text-3xl font-extrabold text-[#1a2d56]" data-testid="lead-score">{lead.commercial_score}<span className="text-base text-[#9aa6c0]">/100</span></div>
            <Badge cls={CATEGORY_STYLE[lead.category]}>{lead.category}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <span className="px-3 py-1.5 rounded-full bg-[#1a2d56] text-white text-sm font-semibold">{lead.profile_primary_name}</span>
          {lead.profile_secondary_name && <span className="px-3 py-1.5 rounded-full bg-[#f0f2f8] text-[#1a2d56] text-sm font-semibold">{lead.profile_secondary_name}</span>}
          <span className="px-3 py-1.5 rounded-full bg-[#d7de3b]/30 text-[#1a2d56] text-sm font-semibold">{lead.source}</span>
          {lead.member_status && (
            <span data-testid="lead-member-status" className={`px-3 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 ${memberBadgeClass(lead.member_status)}`}>
              <BadgeCheck size={14} /> {lead.member_status_label}
            </span>
          )}
        </div>
        {lead.safety_warning && (
          <div className="mt-4 flex gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <AlertTriangle size={18} className="shrink-0 text-amber-500" />
            Vigilance : douleur importante signalée. Vérifier la nécessité d’un avis médical/paramédical.
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
        <h2 className="font-head font-bold text-[#1a2d56] mb-3">Recommandations</h2>
        <Info label="Première étape conseillée" value={lead.first_step} />
        <Info label="Prochaine action" value={lead.next_action} />
        <Info label="Description prospect" value={lead.lead_description} />
        {Array.isArray(lead.advice) && lead.advice.length > 0 && (
          <div className="mb-3" data-testid="lead-advice">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#9aa6c0] flex items-center gap-1.5"><Lightbulb size={13} /> Conseils personnalisés (vus par le prospect)</span>
            <ul className="mt-1.5 space-y-1.5">
              {lead.advice.map((a) => (
                <li key={a} className="text-[#4b5b7c] text-sm flex gap-2 leading-relaxed">
                  <span className="text-[#d7de3b] font-bold">•</span> {a}
                </li>
              ))}
            </ul>
          </div>
        )}
        <Info label="Message interne équipe" value={lead.internal_message} />
        <div className="mt-4 rounded-xl bg-[#f8f9fc] border border-[#e2e8f0] p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-[#1a2d56] flex items-center gap-2"><MessageSquare size={15} /> Script d’appel conseillé</span>
            <button onClick={onCopyScript} data-testid="copy-script" className="text-[#4b5b7c] hover:text-[#1a2d56] inline-flex items-center gap-1 text-sm"><Copy size={14} /> Copier</button>
          </div>
          <p className="text-[#4b5b7c] text-sm leading-relaxed italic">“{lead.call_script}”</p>
        </div>
      </div>

      {/* Profile snapshot */}
      <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
        <h2 className="font-head font-bold text-[#1a2d56] mb-4">Synthèse du profil</h2>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
          <Info label="Objectif" value={ex.objectif} inline />
          <Info label="Frein principal" value={ex.frein} inline />
          <Info label="Douleur / limitation" value={ex.douleur} inline />
          <Info label="Zone concernée" value={ex.zone} inline />
          <Info label="Motivation" value={ex.motivation} inline />
          <Info label="Objectif plaisir" value={ex.plaisir} inline />
          <Info label="Disponibilité" value={ex.disponibilite} inline />
          <Info label="Moment préféré" value={ex.moment} inline />
          <Info label="Urgence" value={ex.urgence} inline />
          <Info label="Accompagnement" value={ex.accompagnement} inline />
        </div>
      </div>

      {/* Full answers */}
      <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
        <h2 className="font-head font-bold text-[#1a2d56] mb-4">Réponses au questionnaire</h2>
        <div className="space-y-3">
          {(lead.answers_readable || []).map((a) => (
            <div key={a.question} className="border-b border-[#eef1f6] pb-3 last:border-0">
              <p className="text-sm text-[#9aa6c0]">{a.question}</p>
              <p className="text-[#1a2d56] font-medium">{a.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
